import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import type { PublicView } from '@sh/shared'
import { Board, POWER_LABEL } from '../components/Board.js'
import { Cinematic } from '../components/Cinematic.js'
import { Connecting } from '../components/Connecting.js'
import { DoveIcon, VictoryIcon } from '../components/Icons.js'
import { QrCode } from '../components/QrCode.js'
import { Attribution } from '../components/Attribution.js'
import { useBoardGame } from '../net/useGame.js'
import { useGameEvents } from '../net/useGameEvents.js'
import { useWakeLock } from '../net/useDevice.js'
import { SkinProvider, useAsset } from '../skin/SkinContext.js'

/**
 * Everything the phones announce, the room's screen announces too.
 *
 * It used to take only an execution and the win, on the reasoning that a table
 * screen is a reference and should not interrupt itself. In practice the room
 * was watching the big screen and the moment was happening on ten small ones:
 * the policy that just went down is the single thing everybody turns to look
 * for, and it was the one thing this screen would not show them. The board
 * behind a scene dims rather than disappearing, and every scene draws its own
 * countdown, so nothing here strands a table waiting on a television.
 */
const BOARD_EVENTS = {} as const

/**
 * What the room should be told is happening right now, in three pieces rather
 * than one sentence. A single line of grey text was accurate and nobody read
 * it from across a room; a kicker sets the phase in one word, the detail names
 * who everyone is waiting on, and the meter turns "4 of 7 ballots in" into
 * something countable at a glance.
 */
interface Status {
  /** One word for the phase, set in the bar's accent. */
  kicker: string
  /** The sentence, with the names that matter already picked out. */
  detail: ReactNode
  /** Phases that are waiting on a count of people get a dot meter. */
  progress?: { done: number; total: number }
}

/** A name the bar leans on, rather than one it merely mentions. */
function Who({ children }: { children: ReactNode }) {
  return <strong className="status__who">{children}</strong>
}

function describe(view: PublicView, name: (id: string) => string): Status {
  const alive = view.players.filter((p) => p.alive).length
  const president = <Who>{name(view.presidentId ?? '')}</Who>
  switch (view.phase) {
    case 'lobby':
      return {
        kicker: 'Lobby',
        detail: <>{view.players.length} seated — waiting for the host to start</>,
      }
    case 'role_reveal':
      return {
        kicker: 'Roles',
        detail: <>Everyone is checking who they are</>,
        progress: { done: view.revealAck.length, total: view.players.length },
      }
    case 'nomination':
      return { kicker: 'Nomination', detail: <>{president} is choosing a Chancellor</> }
    case 'voting':
      return {
        kicker: 'Voting',
        detail: (
          <>
            {president} &amp; <Who>{name(view.nomineeId ?? '')}</Who>
          </>
        ),
        progress: { done: view.ballotsCast.length, total: alive },
      }
    case 'legislative_president':
      return { kicker: 'Legislating', detail: <>{president} is discarding a policy</> }
    case 'legislative_chancellor':
      return {
        kicker: 'Legislating',
        detail: <><Who>{name(view.chancellorId ?? '')}</Who> is enacting a policy</>,
      }
    case 'veto_pending':
      return {
        kicker: 'Veto',
        detail: (
          <>
            <Who>{name(view.chancellorId ?? '')}</Who> proposed a veto — {president} must answer
          </>
        ),
      }
    case 'executive_action':
      return {
        kicker: 'Presidential power',
        // Named. "must use it" left the room asking each other which power the
        // fifth policy had just handed over.
        detail: view.pendingPower ? (
          <>
            {president} must use <Who>{POWER_LABEL[view.pendingPower]}</Who>
          </>
        ) : (
          <>{president} must use it</>
        ),
      }
    case 'game_over':
      return { kicker: 'Game over', detail: <></> }
    default:
      return { kicker: '', detail: <></> }
  }
}

/**
 * The header while the game is running. The title and the join QR are gone by
 * now - everyone who is playing has already joined, and the two of them were
 * taking the top of the screen for the whole game to say something only the
 * first minute of it needed. The code stays as a chip, because the one person
 * who still needs it is whoever's phone just died.
 */
function StatusBar({
  view,
  name,
  code,
}: {
  view: PublicView
  name: (id: string) => string
  code: string
}) {
  const { kicker, detail, progress } = describe(view, name)
  return (
    <header className="table-screen__head status" data-testid="status-bar">
      <span className="status__dot" aria-hidden="true" />
      <div className="status__body">
        <p className="status__kicker">{kicker}</p>
        <p className="status__detail">{detail}</p>
      </div>
      {progress && (
        <span
          className="status__meter"
          aria-label={`${progress.done} of ${progress.total} in`}
        >
          {Array.from({ length: progress.total }, (_, i) => (
            <span key={i} className={`status__pip ${i < progress.done ? 'is-on' : ''}`} />
          ))}
          <span className="status__count" aria-hidden="true">
            {progress.done}/{progress.total}
          </span>
        </span>
      )}
      <span className="status__code" aria-label={`lobby code ${code}`}>
        {code}
      </span>
    </header>
  )
}

/**
 * The header before it starts: the logo as large as it will ever be, and the
 * QR beside it. This is the only screen anybody joins from, and it is the only
 * phase where that is the most useful thing it could be showing.
 */
function JoinHeader({ view, code }: { view: PublicView; code: string }) {
  const joinUrl = `${window.location.origin}/join/${code}`
  const logo = useAsset('logo')
  return (
    <header className="table-screen__head table-screen__head--join">
      <div>
        {logo ? (
          <img className="table-screen__logo" src={logo} alt="Secret Hitler" draggable={false} />
        ) : (
          <h1 className="table-screen__title">Secret Hitler</h1>
        )}
        <p className="table-screen__sub">
          {view.players.length} seated — waiting for the host to start
        </p>
      </div>
      <div className="table-screen__join">
        <QrCode value={joinUrl} size={132} />
        <span className="table-screen__code">{code}</span>
      </div>
    </header>
  )
}

/**
 * And the header at the end, which is not a header. A win is the one thing on
 * this screen worth the whole width, so it takes it, in the winning party's
 * colour. The cinematic has already played by the time anybody reads this - it
 * is a moment, and this is the record of it that stays up while the table
 * argues about what just happened.
 */
function WinBanner({ view }: { view: PublicView }) {
  const liberal = view.winner === 'liberal'
  return (
    <header
      className={`table-screen__head win win--${view.winner ?? 'none'}`}
      data-testid="win-banner"
    >
      {/* The emblem, not a decoration: it is what carries the result to the
          back of the room before anybody has read a word of it. */}
      {view.winner && (
        <span className="win__emblem" aria-hidden="true">
          {liberal ? <DoveIcon /> : <VictoryIcon />}
        </span>
      )}
      <p className="win__kicker">Game over</p>
      <p className="win__title">
        {view.winner ? (liberal ? 'Liberals win' : 'Fascists win') : 'Game abandoned'}
      </p>
      <p className="win__note">Roles are public below</p>
    </header>
  )
}

/**
 * A read-only table screen. It authenticates with nothing but the lobby code
 * and only ever receives the public view, so any number of laptops, tablets or
 * TVs can watch the same game.
 */
export function BoardScreen() {
  const { code = '' } = useParams<{ code: string }>()
  const upper = code.toUpperCase()
  const { view, error, status } = useBoardGame(upper)
  useWakeLock(true)

  if (error) {
    return (
      <main className="table-screen">
        <h1 className="table-screen__code">{upper}</h1>
        <p className="error">{error}</p>
      </main>
    )
  }

  if (!view) {
    return (
      <main className="table-screen" data-testid="board-connecting">
        <Connecting code={upper} status={status} variant="board" />
      </main>
    )
  }

  return (
    <SkinProvider skinId={view.config.skin}>
      <TableScreen view={view} code={upper} />
    </SkinProvider>
  )
}

/**
 * Exported for its stories: it takes a view and a code and nothing else, no
 * router and no socket, so a story can render the real thing rather than an
 * approximation of it that drifts.
 */
export function TableScreen({ view, code }: { view: PublicView; code: string }) {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  // No viewer id: a table screen belongs to nobody, so it never says "you".
  const { event, skip } = useGameEvents(view, null, BOARD_EVENTS)

  return (
    <main
      className={`table-screen ${event ? 'is-cinematic' : ''}`}
      data-phase={view.phase}
      data-testid="board-screen"
    >
      <Cinematic event={event} onSkip={skip} />

      {view.phase === 'lobby' ? (
        <JoinHeader view={view} code={code} />
      ) : view.phase === 'game_over' ? (
        <WinBanner view={view} />
      ) : (
        <StatusBar view={view} name={name} code={code} />
      )}

      <Board view={view} />
      <Attribution />
    </main>
  )
}
